import { isAfter, isBefore } from "date-fns";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import SweetAlert from "sweetalert2";
import ContentWrapper from "../../components/content-wrapper";
import { useKeycloakAuthz } from "../../components/keycloak-provider";
import { SectionHeading } from "../../components/section-heading";
import { useUserProfile } from "../../components/user-profile-provider";
import { useConfig } from "../../lib/config";
import { useApiClient } from "../../lib/http";
import { Reservation } from "../modification/model";
import { KeycloakApi } from "../reservation/keycloakApi";
import {
  APP_COLORS,
  BLACK_LIST_TC_PARTNER,
  PAYMENT_FORM_ACTIONS,
  PAYMENT_TYPES,
  PAYMENT_TYPES_VALUES,
  RERSERVATION_STATUS,
  WHITE_LIST_TC_PARTNER,
} from "../witadmin/Common/constants";
import {
  Api,
  PaymentConfigDto,
  PaymentOrderDto,
  PaymentsApi,
  PaymentsApiChargePayloadDto,
  PaymentsApiRefundPayloadDto,
  PaymentTransactionDto,
  PayNoPainApi,
  PayNoPainTransactionDto,
} from "./apis";
import { DetailView } from "./components/DetailView";
import { computeSummarizeTotal } from "./domain/computeSummarizeTotal";
import { MessageToast, MessageToastProps } from "./components/MessageToast";
import { RoomReservationStatusEnum } from "./components/model";
import { PaymentForm } from "./components/PaymentForm";
import {
  alertMessages,
  alertPaymentMessages,
  messages,
  optionsMessages,
  toastMessages,
} from "./messages";

export interface OperationOption {
  label: string;
  value: string;
}

interface ReservationDetailProps {
  propertyTicker: string;
}

export interface PropertyInfoData {
  name: string;
  contact: {
    phone: string;
    email: string;
    contactEmail: string;
    address: string;
    city: string;
    country: string;
    latitude: number;
    longitude: number;
    postalCode: string;
  };
  maps?: any;
}

export type TransactionDto = {
  id?: string;
  date: string;
  orderNumber: string;
  total: number;
  type?: string;
  description?: string;
  status: string;
  statusInfo?: string;
  creditCardNumber?: string;
  user: string;
  refundTransactionId?: string;
};

const BILLING_DATE: number = 4;

const allowedPaymentTypesToGetPaymentOrder = [
  PAYMENT_TYPES.ADDON_PAYMENTS,
  PAYMENT_TYPES.PAY_NO_PAIN_V2,
  PAYMENT_TYPES.CHECKOUT_PNP,
  PAYMENT_TYPES.ADYEN,
  PAYMENT_TYPES.PAYCOMET,
  PAYMENT_TYPES.PAYCOMET_IVR,
  PAYMENT_TYPES.STRIPE,
  PAYMENT_TYPES.PAY_NO_PAIN_V3,
  PAYMENT_TYPES.FREEDOM_PAY,
  PAYMENT_TYPES.NEMURU,
  PAYMENT_TYPES.SEPTEO_PAYMENTS,
  PAYMENT_TYPES.SIPAY,
  PAYMENT_TYPES.POS,
  PAYMENT_TYPES.PNP_SDK,
];

function itemAmountsForRoomStay(roomStayId: string, amount: number): Record<string, number> {
  return { [roomStayId]: amount };
}

function ReservationDetail({ propertyTicker }: ReservationDetailProps) {
  const intl = useIntl();
  const location = useLocation();
  const config = useConfig();
  const keycloakAuth = useKeycloakAuthz();
  const userProfile = useUserProfile();
  const apiClient = useApiClient();

  const [toast, setToast] = useState<MessageToastProps>({ show: false });
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isAlertHidden, setIsAlertHidden] = useState(true);
  const [alertVariant, setAlertVariant] = useState<"success" | "danger">("success");
  const [alertMessage, setAlertMessage] = useState<MessageDescriptor>();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  // Synchronous in-flight lock for charge/refund: `isProcessing` state only reaches the DOM after
  // the next render, so a fast double click could otherwise start two payment calls.
  const isProcessingRef = useRef(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfigDto>();
  const [reservation, setReservation] = useState<Reservation>();
  const [propertyData, setPropertyData] = useState<PropertyInfoData>();
  const [transactions, setTransactions] = useState<TransactionDto[]>([]);
  const [paymentOrder, setPaymentOrder] = useState<PaymentOrderDto>();
  const [transactionAmount, setTransactionAmount] = useState<number>(0);
  const [transactionDescription, setTransactionDescription] = useState<string>("");
  const [operationSelected, setOperationSelected] = useState<OperationOption>();
  const [transactionId, setTransactionId] = useState<string>();
  const [refreshTransactions, setRefreshTransactions] = useState<boolean>(false);

  /**
   * What the reservation was paid with. Payments overwrites the reservation's own payment method
   * with the wallet the guest used (PAYPAL, GOOGLE_PAY...) on every notification, and the api
   * serialises anything its PaymentType enum does not know as null, so a refunded reservation
   * arrives here with no payment method at all. The configuration resolved from its payment
   * ticker keeps the type in that case.
   */
  const paymentMethodType = useMemo(() => reservation?.paymentMethod || paymentConfig?.type, [
    reservation?.paymentMethod,
    paymentConfig?.type,
  ]);

  const roomStayId = useMemo(() => location.pathname.split("/").pop(), [location.pathname]);
  const authToken = useMemo(() => keycloakAuth.rpt, [keycloakAuth]);
  const username = useMemo(() => userProfile?.email, [userProfile?.email]);

  const paymentsApi = useMemo(() => {
    return new PaymentsApi(config.paymentsApiEndpoint, propertyTicker);
  }, [config, propertyTicker]);

  const api = useMemo(() => new Api(apiClient, propertyTicker), [apiClient, propertyTicker]);

  const payNoPainApi = useMemo(() => {
    return new PayNoPainApi(config.apiEndpoint, propertyTicker);
  }, [config, propertyTicker]);

  const partner = useMemo(() => {
    const userProfileEmail = userProfile?.email;
    return userProfileEmail?.substring(userProfileEmail.indexOf("@") + 1);
  }, [userProfile?.email]);

  const getExhaustivePaymentConfig = useCallback(
    (reservation: Reservation, paymentsConfig: PaymentConfigDto[]) => {
      // Payments overwrites the reservation's payment method with the wallet the guest used
      // (PAYPAL, GOOGLE_PAY...) on every notification, and the api serialises anything its
      // PaymentType enum does not know as null, so this arrives undefined after a refund.
      const paymentMethod = reservation.paymentMethod?.toLowerCase();
      if (reservation.deferred) {
        if (paymentMethod === "deferred") {
          void setToast({
            status: "warning",
            show: true,
            message: toastMessages.deferredPaymentNotUpdatedYet,
          });
          return undefined;
        }
        return paymentsConfig.find(it =>
          [it.ticker, it.type.toLowerCase()].includes(paymentMethod)
        );
      }

      const paymentMethodsToCheck = [
        reservation?.originalPaymentMethod,
        reservation?.paymentTicker,
        paymentMethod,
      ];

      const paymentsConfigFound = paymentMethodsToCheck
        .map(paymentMethod => {
          return paymentsConfig.find(it => it.ticker === paymentMethod);
        })
        .filter((it: PaymentConfigDto | undefined): it is PaymentConfigDto => !!it);

      return paymentsConfigFound.slice(-1).pop();
    },
    []
  );

  const handleSelectOperation = useCallback((e: any) => setOperationSelected(e), []);

  const handleChangeDescription = useCallback(
    (description: string) => setTransactionDescription(description),
    []
  );

  const handleChangeAmount = useCallback((amount: number) => setTransactionAmount(amount), []);

  const handleChangeTransactionId = useCallback(
    (transactionId?: string) => setTransactionId(transactionId),
    []
  );

  const handleToastOnClose = useCallback(() => {
    return (toast.onClose && toast.onClose()) || setToast({ show: false });
  }, [toast]);

  const pendingAmount = useMemo(() => {
    if (!reservation || reservation.roomReservations.length === 0) {
      return 0;
    }
    return reservation.roomReservations
      .map(it => {
        const totalAmount = Number(it.totalAmount.amount);
        const value = it.guaranteePercentage ? totalAmount * it.guaranteePercentage : totalAmount;
        return Number((totalAmount - value).toFixed(2));
      })
      .reduce((total, current) => total + current, 0);
  }, [reservation]);

  const totalAmount = useMemo(() => {
    if (!reservation || reservation.roomReservations.length === 0) {
      return 0;
    }
    return reservation.roomReservations.reduce(
      (total, current) => total + Number(current.totalAmount.amount),
      0
    );
  }, [reservation]);

  const totalNetChargedAmount = useMemo(() => {
    return computeSummarizeTotal(transactions, paymentOrder?.paymentProvider);
  }, [transactions, paymentOrder?.paymentProvider]);

  const isPNPLegacyReservation = useMemo(() => {
    const pnpPaymentMethods = [PAYMENT_TYPES.PAY_NO_PAIN_V2, PAYMENT_TYPES.PAY_NO_PAIN_PROXY];
    if (reservation && paymentMethodType && pnpPaymentMethods.includes(paymentMethodType)) {
      return isBefore(new Date(reservation.placementDate), new Date("2024-10-07")); // new SLS API deploy date
    }
  }, [reservation, paymentMethodType]);

  const getTransactionsByPaymentType = useCallback(
    async (reservation?: Reservation) => {
      if (!reservation?.id) {
        return [];
      }
      if (paymentOrder) {
        const transactions = await paymentsApi.listTransactions(reservation.id, authToken);
        return transactions.data.filter(it => it.status !== "PENDING");
      }
      switch (paymentMethodType) {
        case PAYMENT_TYPES.PAY_NO_PAIN_V2:
        case PAYMENT_TYPES.CHECKOUT_PNP:
        case PAYMENT_TYPES.PNP_SDK:
        case PAYMENT_TYPES.PAY_NO_PAIN_PROXY:
          return payNoPainApi.listTransactions(reservation.id);
        default:
          return [];
      }
    },
    [paymentOrder, paymentsApi, authToken, payNoPainApi, paymentMethodType]
  );

  const fireCreditCardDataLoginAlert = useCallback(() => {
    return SweetAlert.fire({
      title: intl.formatMessage(alertMessages.successTitle),
      icon: "info",
      html: `
        <div class="mb-3">
          <input
            type="text"
            class="form-control"
            id="wit_check_cc_username"
            placeholder="${intl.formatMessage(alertMessages.credentialsModalUsername)}"
          />
        </div>
        <div class="mb-3 text-start">
          <input
            type="password"
            class="form-control"
            id="wit_check_cc_password"
            placeholder="${intl.formatMessage(alertMessages.credentialsModalPassword)}"
          />
        </div>
        <p> ${intl.formatMessage(alertMessages.successText)}
        <b> ${intl.formatMessage(alertMessages.successConsult)} </b> </p>
      `,
      showCancelButton: true,
      confirmButtonText: `${intl.formatMessage(alertMessages.successConsult)}`,
      confirmButtonColor: APP_COLORS.green,
      cancelButtonText: `${intl.formatMessage(alertMessages.successTextBack)}`,
      preConfirm: () => {
        return [
          // @ts-ignore
          document.getElementById("wit_check_cc_username")?.value,
          // @ts-ignore
          document.getElementById("wit_check_cc_password")?.value,
        ];
      },
    });
  }, [intl]);

  const showOperationsForm: boolean = useMemo(() => {
    const availablePaymentMethods = [
      PAYMENT_TYPES.ADDON_PAYMENTS,
      PAYMENT_TYPES.PAYCOMET,
      PAYMENT_TYPES.PAY_NO_PAIN_V2,
      PAYMENT_TYPES.ADYEN,
      PAYMENT_TYPES.STRIPE,
      PAYMENT_TYPES.FREEDOM_PAY,
      PAYMENT_TYPES.PAYCOMET_IVR,
      PAYMENT_TYPES.SEPTEO_PAYMENTS,
      PAYMENT_TYPES.SIPAY,
      PAYMENT_TYPES.PNP_SDK,
    ];
    const isAllowed = !!paymentMethodType && availablePaymentMethods.includes(paymentMethodType);
    const isTpvAllowTokenizedCard =
      paymentMethodType === PAYMENT_TYPES.POS &&
      paymentConfig?.properties?.allowTokenize === "true" &&
      !!paymentOrder?.cardUserToken;

    return !!paymentConfig && (isTpvAllowTokenizedCard || isAllowed);
  }, [paymentMethodType, paymentConfig, paymentOrder]);

  const operationOptions: OperationOption[] = useMemo(() => {
    switch (paymentMethodType) {
      case PAYMENT_TYPES.ADDON_PAYMENTS:
      case PAYMENT_TYPES.PAYCOMET_IVR:
      case PAYMENT_TYPES.FREEDOM_PAY:
        return [
          {
            label: intl.formatMessage(optionsMessages.loadAmount),
            value: PAYMENT_FORM_ACTIONS.LOAD_AMOUNT,
          },
        ];
      case PAYMENT_TYPES.ADYEN:
      case PAYMENT_TYPES.PAYCOMET:
      case PAYMENT_TYPES.STRIPE:
      case PAYMENT_TYPES.PAY_NO_PAIN_V2:
      case PAYMENT_TYPES.SEPTEO_PAYMENTS:
      case PAYMENT_TYPES.SIPAY:
      case PAYMENT_TYPES.POS:
        return [
          {
            label: intl.formatMessage(optionsMessages.loadAmount),
            value: PAYMENT_FORM_ACTIONS.LOAD_AMOUNT,
          },
          {
            label: intl.formatMessage(optionsMessages.refund),
            value: PAYMENT_FORM_ACTIONS.REFUND,
          },
        ];
      // Refund only: a wallet or a PayPal payment leaves no credential on file to charge against
      // later (Paylands answers `cof_txnid: null` for both), so "load amount" has nothing to run on.
      case PAYMENT_TYPES.PNP_SDK:
        return [
          {
            label: intl.formatMessage(optionsMessages.refund),
            value: PAYMENT_FORM_ACTIONS.REFUND,
          },
        ];
      default:
        return [];
    }
  }, [intl, paymentMethodType]);

  const roomReservation = useMemo(() => {
    return reservation?.roomReservations.find(it => it.confirmationId === roomStayId);
  }, [reservation, roomStayId]);

  const roomReservationStatus = useMemo(() => {
    switch (roomReservation?.status) {
      case RoomReservationStatusEnum.RESERVATION:
        return RERSERVATION_STATUS.OK;
      case RoomReservationStatusEnum.PRE_RESERVATION:
      case RoomReservationStatusEnum.REACTIVATION:
        return RERSERVATION_STATUS.PEND;
      case RoomReservationStatusEnum.CANCELLED:
        return RERSERVATION_STATUS.CXL;
      default:
        return RERSERVATION_STATUS.KO;
    }
  }, [roomReservation]);

  const canEditOnValidCheckoutDate = useMemo(() => {
    const currentMonth = new Date().getMonth();

    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const previousMonth = new Date(new Date().setMonth(currentMonth - 1));
    previousMonth.setDate(1);
    previousMonth.setHours(0, 0, 0);

    const afterBillingDate = isAfter(
      new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Madrid" })),
      new Date().setDate(BILLING_DATE)
    );
    // If past the billing day of current month, only allow cancellation for the current month onward
    if (
      !afterBillingDate &&
      roomReservation?.checkOut &&
      isBefore(new Date(roomReservation?.checkOut), previousMonth)
    ) {
      return false;
    }
    // If not past the billing day of current month, allow cancellation for the previous month as well
    return !(
      afterBillingDate &&
      roomReservation?.checkOut &&
      isBefore(new Date(roomReservation?.checkOut), startOfMonth)
    );
  }, [roomReservation?.checkOut]);

  const showEditButton = useMemo(() => {
    if (roomReservationStatus === RERSERVATION_STATUS.CXL) {
      return false;
    }
    return canEditOnValidCheckoutDate;
  }, [roomReservationStatus, canEditOnValidCheckoutDate]);

  const showCCButton = useMemo(() => {
    if (!partner) {
      return false;
    }

    const isOnBlackListPartners = BLACK_LIST_TC_PARTNER.includes(partner);
    const isOnWhiteListPartners = WHITE_LIST_TC_PARTNER[propertyTicker]?.includes(
      userProfile?.email || ""
    );
    const isBookingAffiliate = reservation?.affiliate === "BOOKING";
    const isPendingReservation = roomReservationStatus === RERSERVATION_STATUS.PEND;
    const isValidPaymentStatus =
      reservation?.paymentStatus === "0" ||
      (reservation?.paymentStatus === "2" &&
        reservation.paymentTicker === PAYMENT_TYPES_VALUES.pay_no_pain_proxy);

    return (
      !isBookingAffiliate &&
      !isPendingReservation &&
      isValidPaymentStatus &&
      (!isOnBlackListPartners || isOnWhiteListPartners)
    );
  }, [
    partner,
    propertyTicker,
    userProfile?.email,
    reservation?.affiliate,
    reservation?.paymentStatus,
    reservation?.paymentTicker,
    roomReservationStatus,
  ]);

  /**
   * Handle Show CC Data
   */
  const handleShowCreditCardData = useCallback(async () => {
    if (reservation?.paymentTicker === PAYMENT_TYPES_VALUES.pay_no_pain_proxy) {
      SweetAlert.showLoading();
      try {
        const creditCardUrl = await api.getCreditCardUrl(reservation.id, userProfile?.email);
        SweetAlert.close();
        const { isConfirmed, isDismissed, value } = await fireCreditCardDataLoginAlert();
        if (!isConfirmed || isDismissed) {
          return Promise.resolve(false);
        }
        if (isConfirmed && value) {
          const [username, password] = value;
          try {
            await KeycloakApi.isValidUser(config, username, password);
            window.open(creditCardUrl, "_blank");
          } catch (e) {
            console.error(
              "Credit card re-authentication failed",
              e instanceof Error ? e.message : "unknown error"
            );
            await SweetAlert.fire({
              title: intl.formatMessage(alertMessages.errorTitle),
              icon: "error",
              html: `<p>${intl.formatMessage(alertMessages.credentialsModalInvalidUser)}</p>`,
            });
          }
        }
      } catch (error) {
        console.error(
          "Unable to retrieve credit card data",
          error instanceof Error ? error.message : "unknown error"
        );
        SweetAlert.close();
        await SweetAlert.fire({
          title: intl.formatMessage(alertMessages.warningTitle),
          icon: "warning",
          html: `<p>${intl.formatMessage(alertMessages.warningText)}</p>`,
        });
      }
    } else {
      window.open(
        `https://secure.witbooking.com/login?reservationID=${reservation?.id}&ticker=${propertyTicker}`,
        "_blank"
      );
    }
  }, [reservation, propertyTicker, config, fireCreditCardDataLoginAlert, intl, api, userProfile]);

  /**
   * Handle charge payment
   */
  const handleProcessChargeAmount = useCallback(async () => {
    if (!reservation?.id) {
      throw new Error("reservation id is required");
    }
    if (!paymentConfig?.ticker) {
      throw new Error("payment method configured is required");
    }
    if (!roomStayId) {
      throw new Error("room stay id is required");
    }
    const payload: PaymentsApiChargePayloadDto = {
      reservationId: reservation.id,
      amount: Number(transactionAmount),
      description: transactionDescription,
      email: username!,
    };
    if (paymentOrder) {
      switch (paymentOrder.paymentProvider) {
        case PAYMENT_TYPES.ADDON_PAYMENTS:
          return paymentsApi.addonPaymentsCharge(
            {
              ...payload,
              itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
            },
            authToken,
            paymentConfig.ticker
          );
        case PAYMENT_TYPES.PAYCOMET:
        case PAYMENT_TYPES.PAYCOMET_IVR:
          return paymentsApi.paycometCharge(
            {
              ...payload,
              itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
            },
            authToken,
            paymentConfig.ticker
          );
        case PAYMENT_TYPES.ADYEN:
          return paymentsApi.adyenCharge(
            {
              ...payload,
              itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
            },
            authToken,
            paymentConfig.ticker
          );
        case PAYMENT_TYPES.STRIPE:
          return paymentsApi.stripeCharge(
            {
              ...payload,
              itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
            },
            authToken,
            paymentConfig.ticker
          );
        case PAYMENT_TYPES.PAY_NO_PAIN_V3:
          return paymentsApi.payNoPainV3Charge(
            {
              ...payload,
              itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
            },
            authToken,
            paymentConfig.ticker
          );
        case PAYMENT_TYPES.FREEDOM_PAY:
          return paymentsApi.freedomCharge(
            {
              ...payload,
              reservation,
              itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
            },
            authToken,
            paymentConfig.ticker
          );
        case PAYMENT_TYPES.SEPTEO_PAYMENTS:
          return paymentsApi.septeoPaymentsCharge(
            {
              ...payload,
              locale: reservation.language,
              currency: reservation.roomReservations[0].totalAmount.currency,
              itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
            },
            authToken,
            paymentConfig.ticker
          );
        case PAYMENT_TYPES.SIPAY:
          return paymentsApi.sipayCharge(
            {
              ...payload,
              itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
            },
            authToken,
            paymentConfig.ticker
          );
        case PAYMENT_TYPES.POS:
        case PAYMENT_TYPES.REDSYS:
          return paymentsApi.redsysCharge(
            {
              ...payload,
              itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
            },
            authToken,
            paymentConfig.ticker
          );
        default:
          throw new Error("invalid payment provider given");
      }
    }
    // retro-compatible
    switch (paymentMethodType) {
      case PAYMENT_TYPES.PAY_NO_PAIN_V2:
      case PAYMENT_TYPES.PAY_NO_PAIN_PROXY:
        return payNoPainApi.charge(payload);
      default:
        throw new Error("invalid payment provider given");
    }
  }, [
    reservation,
    paymentMethodType,
    paymentConfig?.ticker,
    transactionAmount,
    transactionDescription,
    username,
    paymentOrder,
    paymentsApi,
    authToken,
    payNoPainApi,
    roomStayId,
  ]);

  const sanitizeTransactions = useCallback(
    (transactions: PayNoPainTransactionDto[] | PaymentTransactionDto[]): TransactionDto[] => {
      return transactions.map((it: PayNoPainTransactionDto | PaymentTransactionDto) => {
        if ("paymentProvider" in it) {
          const isStatusSuccess = it.status === "SUCCESS" || it.normalizedStatus === "SUCCESS";
          // New payments
          return {
            id: it.id,
            date: it.createdAt,
            orderNumber: it.orderId,
            total: it.amount,
            type: it.normalizedOperationType,
            description: it.description || it.operationTypeDescription,
            status: isStatusSuccess ? "OK" : it.status,
            statusInfo: isStatusSuccess ? it.paymentMessage : undefined,
            creditCardNumber: it.cardNumber,
            user: it.user,
            refundTransactionId: it.refundTransactionId,
          };
        }
        // retro-compatible
        return {
          id: it.id,
          status: it.status,
          date: it.creationDate,
          orderNumber: it.orderNumber,
          total: it.amount.amount,
          type: it.operationType,
          description: it.description,
          creditCardNumber: it.cardPan,
          user: it.user,
          statusInfo: it.status !== "OK" ? it.responseCode : undefined,
        };
      });
    },
    []
  );

  const resetForm = useCallback(() => {
    setIsAlertHidden(true);
    setTransactionId("");
    setTransactionAmount(0);
    setTransactionDescription("");
    setRefreshTransactions(true);
  }, []);

  const handleChargeSuccess = useCallback(() => {
    setIsAlertHidden(false);
    setAlertMessage(alertPaymentMessages.paymentChargeSuccess);
    setAlertVariant("success");
    setRefreshTransactions(true);
    setTimeout(() => resetForm(), 5000);
  }, [resetForm]);

  const handleChargeError = useCallback(() => {
    setIsAlertHidden(false);
    setAlertMessage(alertPaymentMessages.paymentChargeError);
    setAlertVariant("danger");
  }, []);

  const handleRefundSuccess = useCallback(() => {
    setIsAlertHidden(false);
    setAlertMessage(alertPaymentMessages.paymentRefundSuccess);
    setAlertVariant("success");
    setRefreshTransactions(true);
    setTimeout(() => resetForm(), 5000);
  }, [resetForm]);

  const handleRefundError = useCallback(() => {
    setIsAlertHidden(false);
    setAlertMessage(alertPaymentMessages.paymentRefundError);
    setAlertVariant("danger");
  }, []);

  /**
   * Handle process transaction
   */
  const handleProcessTransaction = useCallback(async () => {
    if (!reservation?.id) {
      throw new Error("reservation is required");
    }
    switch (operationSelected?.value) {
      case PAYMENT_FORM_ACTIONS.LOAD_AMOUNT:
        if (isProcessingRef.current) {
          return;
        }
        isProcessingRef.current = true;
        setIsProcessing(true);
        try {
          await handleProcessChargeAmount();
          handleChargeSuccess();
        } catch (e) {
          handleChargeError();
        } finally {
          isProcessingRef.current = false;
          setIsProcessing(false);
        }
        break;
      case PAYMENT_FORM_ACTIONS.REFUND:
        setModalOpen(true);
        break;
      default:
        setIsProcessing(false);
        throw new Error("transaction operation selected is not allowed");
    }
  }, [
    reservation?.id,
    operationSelected?.value,
    handleProcessChargeAmount,
    handleChargeSuccess,
    handleChargeError,
  ]);

  const handleOnConfirmRefund = useCallback(async () => {
    if (!reservation?.id) {
      throw new Error("reservation id is required");
    }
    if (!paymentConfig?.ticker) {
      throw new Error("payment method configured is required");
    }
    if (!transactionId) {
      throw new Error("transaction id is required");
    }
    if (!roomStayId) {
      throw new Error("room stay id is required");
    }
    if (isProcessingRef.current) {
      return;
    }
    isProcessingRef.current = true;
    setIsProcessing(true);
    const payload: PaymentsApiRefundPayloadDto = {
      reservationId: reservation.id,
      amount: Number(transactionAmount),
      description: "BOOKING_ENGINE_REFUND",
      email: username!,
      transactionId,
    };
    try {
      if (paymentOrder) {
        switch (paymentOrder?.paymentProvider) {
          case PAYMENT_TYPES.PAY_NO_PAIN_V2:
          case PAYMENT_TYPES.PAY_NO_PAIN_V3:
            await paymentsApi.payNoPainV3Refund(
              {
                ...payload,
                itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
              },
              authToken,
              paymentConfig.ticker
            );
            return handleRefundSuccess();
          case PAYMENT_TYPES.PAY_NO_PAIN_SDK:
            await paymentsApi.payNoPainSdkRefund(payload, authToken, paymentConfig.ticker);
            return handleRefundSuccess();
          case PAYMENT_TYPES.PAYCOMET:
            await paymentsApi.paycometRefund(
              {
                ...payload,
                itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
              },
              authToken,
              paymentConfig.ticker
            );
            return handleRefundSuccess();
          case PAYMENT_TYPES.STRIPE:
            await paymentsApi.stripeRefund(
              {
                ...payload,
                itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
              },
              authToken,
              paymentConfig.ticker
            );
            return handleRefundSuccess();
          case PAYMENT_TYPES.ADYEN:
            await paymentsApi.adyenRefund(
              {
                ...payload,
                description: "CUSTOMER REQUEST",
                itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
              },
              authToken,
              paymentConfig.ticker
            );
            return handleRefundSuccess();
          case PAYMENT_TYPES.SEPTEO_PAYMENTS:
            await paymentsApi.septeoPaymentsRefund(
              {
                ...payload,
                locale: reservation.language,
                currency: reservation.roomReservations[0].totalAmount.currency,
                itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
              },
              authToken,
              paymentConfig.ticker
            );
            return handleRefundSuccess();
          case PAYMENT_TYPES.SIPAY:
            await paymentsApi.sipayRefund(
              {
                ...payload,
                itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
                hotelTicker: propertyTicker,
              },
              authToken,
              paymentConfig.ticker
            );
            return handleRefundSuccess();
          case PAYMENT_TYPES.POS:
          case PAYMENT_TYPES.REDSYS:
            await paymentsApi.redsysRefund(
              {
                ...payload,
                itemAmounts: itemAmountsForRoomStay(roomStayId, payload.amount),
              },
              authToken,
              paymentConfig.ticker
            );
            return handleRefundSuccess();
          default:
            throw new Error("invalid payment provider given");
        }
      }
      // retro-compatible
      switch (paymentMethodType) {
        case PAYMENT_TYPES.PAY_NO_PAIN_V2:
        case PAYMENT_TYPES.PAY_NO_PAIN_PROXY:
          await payNoPainApi.refund(payload);
          return handleRefundSuccess();
        default:
          throw new Error("invalid payment provider given");
      }
    } catch (e) {
      handleRefundError();
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
      setModalOpen(false);
    }
  }, [
    authToken,
    handleRefundError,
    handleRefundSuccess,
    payNoPainApi,
    paymentConfig?.ticker,
    paymentOrder,
    paymentsApi,
    propertyTicker,
    reservation?.id,
    reservation?.language,
    paymentMethodType,
    reservation?.roomReservations,
    transactionAmount,
    transactionId,
    username,
    roomStayId,
  ]);

  /**
   * Get Payment configuration
   */
  useEffect(() => {
    if (reservation) {
      const asyncFun = async () => {
        const response: PaymentConfigDto[] = await api.getBackofficePaymentConfigs();
        const paymentConfig = getExhaustivePaymentConfig(reservation, response);
        setPaymentConfig(paymentConfig);
      };
      asyncFun().catch(() =>
        setToast({ status: "error", show: true, message: toastMessages.getPaymentConfigsError })
      );
    }
  }, [reservation, propertyTicker, api, getExhaustivePaymentConfig]);

  /**
   * Get Reservation and Property Data
   */
  useEffect(() => {
    const asyncFun = async () => {
      const [reservationResponse, propertyDataResponse] = await Promise.all([
        api.getReservation(roomStayId!),
        api.getPropertyData(),
      ]);
      setReservation(reservationResponse);
      // TODO: we should not depend on the engine-api cache / propertyData for this, we should put this info in the GetPropertyInfo endpoint from BM
      setPropertyData(propertyDataResponse[propertyTicker]);
    };
    asyncFun()
      .catch(() =>
        setToast({
          status: "error",
          show: true,
          message: toastMessages.getReservationAndPropertyDataError,
        })
      )
      .finally(() => setIsLoading(false));
  }, [propertyTicker, roomStayId, authToken, config, api]);

  /**
   * Get Sorted Transactions
   */
  useEffect(() => {
    if (reservation || refreshTransactions) {
      setIsLoadingTransactions(true);
      const asyncFun = async () => {
        const transactionsResponse = await getTransactionsByPaymentType(reservation);
        const transactionsSanitized = sanitizeTransactions(transactionsResponse);
        const sortedTransactions = transactionsSanitized.sort((a, b) => {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        });
        setTransactions(sortedTransactions);
      };
      asyncFun()
        .catch(e => {
          console.error(e.code, e.statusCode);
          setToast({
            status: "error",
            show: true,
            message: toastMessages.getTransactionsError,
          });
        })
        .finally(() => {
          setIsLoadingTransactions(false);
          setRefreshTransactions(false);
        });
    }
  }, [
    reservation,
    paymentsApi,
    getTransactionsByPaymentType,
    sanitizeTransactions,
    refreshTransactions,
  ]);

  /**
   * Get Payment Order
   */
  useEffect(() => {
    if (
      reservation?.id &&
      !isPNPLegacyReservation &&
      !!paymentMethodType &&
      allowedPaymentTypesToGetPaymentOrder.includes(paymentMethodType)
    ) {
      const asyncFun = async () => {
        const response = await paymentsApi.getPaymentOrder(reservation.id, authToken);
        setPaymentOrder(response);
      };
      asyncFun().catch(() => console.warn("reservation doesn't support MIT charge"));
    }
  }, [reservation, authToken, paymentsApi, isPNPLegacyReservation, paymentMethodType]);

  return (
    <ContentWrapper>
      <MessageToast {...toast} onClose={handleToastOnClose} />
      <SectionHeading>{intl.formatMessage(messages.title, { roomStayId })}</SectionHeading>
      {!isLoading && reservation && propertyData && (
        <>
          <DetailView
            roomStayId={roomStayId!}
            reservation={reservation}
            propertyData={propertyData}
            showEditButton={showEditButton}
            showCCButton={showCCButton}
            onShowCreditCardData={handleShowCreditCardData}
          />

          <PaymentForm
            isNewPayment={paymentOrder?.paymentProvider}
            reservationId={reservation.id}
            operations={operationOptions}
            transactions={transactions}
            transactionAmount={transactionAmount}
            setTransactionAmount={setTransactionAmount}
            transactionDescription={transactionDescription}
            transactionId={transactionId}
            onChangeAmount={handleChangeAmount}
            onChangeDescription={handleChangeDescription}
            onChangeTransactionId={handleChangeTransactionId}
            operationSelected={operationSelected}
            onSelectOperation={handleSelectOperation}
            onConfirmTransaction={handleProcessTransaction}
            onConfirmRefund={handleOnConfirmRefund}
            setIsProcessing={setIsProcessing}
            isProcessing={isProcessing}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            isAlertHidden={isAlertHidden}
            alertVariant={alertVariant}
            alertMessage={alertMessage}
            totalAmount={totalAmount}
            pendingAmount={pendingAmount}
            totalNetChargedAmount={totalNetChargedAmount}
            isLoadingTransactions={isLoadingTransactions}
            resetForm={resetForm}
            showOperationsForm={showOperationsForm}
            paymentProvider={paymentOrder?.paymentProvider}
          />
        </>
      )}
    </ContentWrapper>
  );
}

export default ReservationDetail;
